// calendarSources — the client half of the system-calendar source registry.
// Spec: docs/specs/system-calendar.md · SQL half: mig 20261017240000
// (`calendar_sources`).
//
// The registry is deliberately declared TWICE. The server needs the module
// gate, the tense and the metric unit to build and filter the union; the client
// needs an icon, a colour and a way to say "14 deliveries · 41.2 t" in English.
// Neither half can hold the other's data usefully — you cannot put a Lucide
// component in a Postgres row, and the RPC cannot be gated from TypeScript.
//
// So they are pinned instead: calendarSources.test.ts parses the migration's
// seed INSERT and asserts the two key sets are identical and that every
// module_key is a real MODULES[] key. A source added on one side only fails CI
// rather than rendering as an unlabelled grey chip nobody can explain.
//
// TENSE is what makes the grid readable. Colour carries tense; saturation
// carries urgency within `due`. Everything on today's page is coloured by
// urgency alone, which stops working the moment the past is on the same grid —
// a day with 200 completed deliveries would glow like a day with 200 missed
// deadlines.
import type { StatusObject } from '@/lib/statusTone'
import {
  Truck, Factory, Package, Send, FlaskConical,
  ShieldCheck, FileText, FileSearch, Scale, GraduationCap, Wrench,
  SprayCan, Bug, Sprout, Droplets, AlertTriangle, Leaf, Flower2,
  type LucideIcon,
} from 'lucide-react'

export type CalendarTense = 'done' | 'due' | 'planned'

/** The five urgency bands from packhouse_calendar_events, plus 'done'. */
export type CalendarBand = 'overdue' | 'd7' | 'd14' | 'd30' | 'current' | 'done'

export interface CalendarSourceDef {
  /** Matches calendar_sources.source_type. */
  key:       string
  /**
   * Matches calendar_sources.module_key. Used by the day panel only, to decide
   * whether a row is a link (can the viewer open the page?). It is NOT the
   * server's gate for every source: the calibration arm admits compliance OR qc.
   */
  moduleKey: string
  tense:     CalendarTense
  /** Short label for a chip and the legend. Matches calendar_sources.label. */
  label:     string
  icon:      LucideIcon
  /**
   * Plural noun for the aggregate chip: "14 deliveries". Client-only — the
   * server's `label` is the column heading, this is the sentence.
   */
  noun:      string
  /** Matches calendar_sources.metric_unit; null = count only. */
  unit:      string | null
  /** Matches calendar_sources.is_personal (POPIA — manager+ only). */
  personal:  boolean
}

// Order here is presentation order within a day cell; the server carries its
// own sort_order for the same purpose, and the test pins them equal.
export const CALENDAR_SOURCES: CalendarSourceDef[] = [
  // ── due ────────────────────────────────────────────────────────────────
  { key: 'compliance_item',    moduleKey: 'compliance',  tense: 'due',  label: 'Compliance',  icon: ShieldCheck,    noun: 'compliance items', unit: null,      personal: false },
  { key: 'cert_doc_expiry',    moduleKey: 'compliance',  tense: 'due',  label: 'Cert expiry', icon: FileText,       noun: 'certificates',     unit: null,      personal: false },
  { key: 'cert_doc_review',    moduleKey: 'compliance',  tense: 'due',  label: 'Doc review',  icon: FileSearch,     noun: 'document reviews', unit: null,      personal: false },
  { key: 'calibration',        moduleKey: 'compliance',  tense: 'due',  label: 'Calibration', icon: Scale,          noun: 'calibrations',     unit: null,      personal: false },
  { key: 'training',           moduleKey: 'compliance',  tense: 'due',  label: 'Training',    icon: GraduationCap,  noun: 'training renewals',unit: null,      personal: true  },
  { key: 'maintenance_wo',     moduleKey: 'maintenance', tense: 'due',  label: 'Maintenance', icon: Wrench,         noun: 'work orders',      unit: null,      personal: false },
  { key: 'loading_mission',    moduleKey: 'dispatch',    tense: 'due',  label: 'Truck load',  icon: Truck,          noun: 'truck loads',      unit: null,      personal: false },
  // ── done ───────────────────────────────────────────────────────────────
  { key: 'delivery_received',  moduleKey: 'deliveries',  tense: 'done', label: 'Deliveries',  icon: Truck,          noun: 'deliveries',       unit: 'bins',    personal: false },
  { key: 'run',                moduleKey: 'runs',        tense: 'done', label: 'Runs',        icon: Factory,        noun: 'runs',             unit: 'kg',      personal: false },
  { key: 'pallets_packed',     moduleKey: 'pallets',     tense: 'done', label: 'Packed',      icon: Package,        noun: 'pallets packed',   unit: 'cartons', personal: false },
  { key: 'pallets_dispatched', moduleKey: 'dispatch',    tense: 'done', label: 'Dispatched',  icon: Send,           noun: 'pallets out',      unit: null,      personal: false },
  { key: 'qc_run',             moduleKey: 'qc',          tense: 'done', label: 'QC',          icon: FlaskConical,   noun: 'QC runs',          unit: 'fails',   personal: false },
  // ── done · farm (Phase 3, mig 20261020130000) ───────────────────────────
  // Every farm date column but the load handshake is a DATE, so these arms
  // bucket on the calendar day the operator wrote, not a timestamp.
  { key: 'spray_applied',      moduleKey: 'sprays',            tense: 'done', label: 'Sprays',       icon: SprayCan,      noun: 'sprays',                  unit: 'ha',         personal: false },
  { key: 'scouting',           moduleKey: 'scouting',          tense: 'done', label: 'Scouting',     icon: Bug,           noun: 'scouting observations',   unit: null,         personal: false },
  { key: 'harvest_pick',       moduleKey: 'harvest_picks',     tense: 'done', label: 'Picks',        icon: Sprout,        noun: 'harvest picks',           unit: 'containers', personal: false },
  { key: 'fert_applied',       moduleKey: 'fert_applications', tense: 'done', label: 'Fertilising',  icon: Leaf,          noun: 'fertiliser applications', unit: null,         personal: false },
  { key: 'irrigation',         moduleKey: 'water_schedule',    tense: 'done', label: 'Irrigation',   icon: Droplets,      noun: 'irrigations',             unit: 'mm',         personal: false },
  { key: 'farm_problem',       moduleKey: 'farm_problems',     tense: 'done', label: 'Problems',     icon: AlertTriangle, noun: 'farm problems',           unit: null,         personal: false },
  { key: 'phenology',          moduleKey: 'phenology',         tense: 'done', label: 'Growth Stages', icon: Flower2,       noun: 'growth-stage logs',       unit: null,         personal: false },
  { key: 'farm_load',          moduleKey: 'farm_loads',        tense: 'done', label: 'Loads',        icon: Truck,         noun: 'loads to packhouse',      unit: 'containers', personal: false },
  // ── planned · farm ─────────────────────────────────────────────────────
  { key: 'spray_task',         moduleKey: 'sprays',            tense: 'planned', label: 'Spray tasks',  icon: SprayCan,   noun: 'spray tasks',             unit: null,         personal: false },
  { key: 'fert_planned',       moduleKey: 'fert_applications', tense: 'planned', label: 'Fert planned', icon: Leaf,       noun: 'planned fertilisings',    unit: null,         personal: false },
]

const BY_KEY = new Map(CALENDAR_SOURCES.map(s => [s.key, s]))

/**
 * A source the client does not know about — the server has been seeded with a
 * source this build predates. Render it plainly rather than crashing: a
 * deployment where the migration is ahead of the bundle is normal for a few
 * minutes, and an unknown chip is better than a white screen.
 */
export function calendarSource(key: string): CalendarSourceDef {
  return BY_KEY.get(key) ?? {
    key, moduleKey: '', tense: 'done', label: key, icon: FileText,
    noun: key.replace(/_/g, ' '), unit: null, personal: false,
  }
}

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

// Tense → chip skin. `due` is overridden per-band below; `done` and `planned`
// are flat, because neither has urgency to express.
const TENSE_CHIP: Record<CalendarTense, string> = {
  done:    'bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-750',
  due:     'bg-gray-700 text-gray-100 border border-gray-600',
  planned: 'bg-sky-900/50 text-sky-200 border border-sky-800/60 border-dashed hover:bg-sky-900/70',
}

// Band → chip skin, for `due` rows only. Kept byte-compatible with the tones
// the current page ships so the rebuild is not also a visual redesign of the
// deadline plane people already read.
const BAND_CHIP: Record<CalendarBand, string> = {
  overdue: 'bg-red-700/80 light:bg-red-700 text-red-100 light:text-on-fill hover:bg-red-700 light:hover:brightness-90',
  d7:      'bg-orange-700/80 light:bg-orange-700 text-orange-100 light:text-on-fill hover:bg-orange-700 light:hover:brightness-90',
  d14:     'bg-amber-700/70 light:bg-amber-700 text-amber-100 light:text-on-fill hover:bg-amber-700 light:hover:brightness-90',
  d30:     'bg-lime-800/70 text-lime-100 hover:bg-lime-800',
  current: 'bg-gray-700/80 text-gray-200 hover:bg-gray-700',
  done:    TENSE_CHIP.done,
}

/** Which StatusObject words a due row's raw `status` belongs to (R1). A source
 *  not listed keeps the band badge. */
export const DUE_STATUS_OBJECT: Record<string, StatusObject> = {
  compliance_item: 'compliance_item',
  cert_doc_expiry: 'certification',
  cert_doc_review: 'certification',
  calibration:     'equipment',
  training:        'training',
  maintenance_wo:  'work_order',
  loading_mission: 'mission',
}

export const BAND_BADGE: Record<CalendarBand, string> = {
  overdue: 'bg-red-900/60 text-red-200 border border-red-700/50',
  d7:      'bg-orange-900/50 text-orange-200 border border-orange-700/40',
  d14:     'bg-amber-900/40 text-amber-200 border border-amber-700/40',
  d30:     'bg-lime-900/40 text-lime-200 border border-lime-700/40',
  current: 'bg-gray-800 text-gray-300 border border-gray-700',
  done:    'bg-gray-800 text-gray-400 border border-gray-700',
}

/** The badge a PLANNED row wears in the day drawer (FM11 UF-10): its status,
 *  and for a task whose day has passed, how long it has been waiting — the
 *  same fact the agenda row says as "6d ago" in red. */
export function plannedBadgeText(status: string | null, day: string, today: string, band: CalendarBand): string {
  const word = status === 'IN_PROGRESS' ? 'In progress' : 'Planned'
  if (band !== 'overdue') return word
  const n = Math.round((Date.parse(today + 'T00:00:00Z') - Date.parse(day + 'T00:00:00Z')) / 86_400_000)
  return n > 0 ? `${word} · ${n}d overdue` : word
}

/** Whole days a day-drawer row's day lies before today; 0 when it is today or later. */
function daysOverdue(day: string, today: string): number {
  const n = Math.round((Date.parse(today + 'T00:00:00Z') - Date.parse(day + 'T00:00:00Z')) / 86_400_000)
  return n > 0 ? n : 0
}

/** The date badge a DUE row wears beside its status pill, in its band colour:
 *  "Nd overdue" once late, "in Nd" while d7, d14 or d30 (the band stays a date
 *  scale). Nothing for a row with no band to speak of. */
export function dueBadgeText(day: string, today: string, band: CalendarBand): string | null {
  if (band === 'overdue') { const n = daysOverdue(day, today); return n > 0 ? `${n}d overdue` : null }
  if (band !== 'd7' && band !== 'd14' && band !== 'd30') return null
  const n = Math.round((Date.parse(day + 'T00:00:00Z') - Date.parse(today + 'T00:00:00Z')) / 86_400_000)
  return n === 0 ? 'today' : n === 1 ? 'tomorrow' : `in ${n}d`
}

/** Most-urgent-first. Drives within-day ordering AND the "never collapse a
 *  deadline behind twelve deliveries" rule. */
export const BAND_RANK: Record<CalendarBand, number> = {
  overdue: 0, d7: 1, d14: 2, d30: 3, current: 4, done: 5,
}

export function chipSkin(tense: CalendarTense, band: CalendarBand | null): string {
  if (tense === 'due') return BAND_CHIP[band ?? 'current']
  return TENSE_CHIP[tense]
}

// ---------------------------------------------------------------------------
// Metric formatting
// ---------------------------------------------------------------------------

/**
 * Render a source's metric for a chip. kg becomes tonnes past 1 000 because a
 * packing day is six figures of kg and "41.2 t" is the number people say.
 * Returns null when there is nothing worth showing — a zero metric is real
 * (a run that has not packed yet) but adding "· 0 kg" to every in-flight run
 * is noise, not information.
 */
export function formatMetric(value: number | null, unit: string | null): string | null {
  if (value === null || unit === null || value === 0) return null
  if (unit === 'kg') {
    return value >= 1000
      ? `${(value / 1000).toLocaleString('en-ZA', { maximumFractionDigits: 1 })} t`
      : `${Math.round(value).toLocaleString('en-ZA')} kg`
  }
  return `${Math.round(value).toLocaleString('en-ZA')} ${unit}`
}

/**
 * The aggregate chip's text. One event names itself ("R-0912 · Bergaret");
 * several are counted ("14 deliveries · 312 bins"). The single-row case is why
 * the summary RPC returns sample_label at all — a cell reading "1 run" when it
 * could read the run number is a worse cell.
 */
/** A day's count on a phone-width month chip: the digits the ~28px it has
 *  can hold. Below 1 000 the count is exact; from there it is "1.4k" / "12k".
 *  A 3-digit count in a `truncate` chip lost its LAST digit with no ellipsis —
 *  109 pallets read "10", a silently wrong number (packhouse re-render N20). */
export function compactCount(n: number): string {
  if (n < 1000) return String(n)
  const k = n / 1000
  return `${k < 10 ? (Math.floor(k * 10) / 10).toString() : Math.floor(k).toString()}k`
}

export function chipText(
  count: number, sampleLabel: string | null, source: CalendarSourceDef,
  metric: number | null,
): string {
  if (count === 1 && sampleLabel) return sampleLabel
  const m = formatMetric(metric, source.unit)
  return m ? `${count} ${source.noun} · ${m}` : `${count} ${source.noun}`
}
